import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { sendEVMTransaction, EVMTransactionError } from '../utils/evm-transaction';

type TransactionStatus = 'idle' | 'preparing' | 'awaiting-signature' | 'broadcasting' | 'confirming' | 'success' | 'error';

interface PaymentSession {
  sessionId: string;
  paymentAddress: string;
  amount: number;
  expiresAt: string;
  tokensToReceive: number;
  network: string;
  chainId?: number;
  tokenAddress?: string;
  tokenSymbol?: string;
  decimals?: number;
  amountBaseUnits?: string;
}

interface TransactionState {
  // Transaction state
  txStatus: TransactionStatus;
  txHash: string;
  txError: string;
  showQR: boolean;

  // Wallet connection
  pendingPaymentAfterWalletConnect: boolean;

  // Actions
  setTxStatus: (status: TransactionStatus) => void;
  setTxHash: (hash: string) => void;
  setTxError: (error: string) => void;
  setShowQR: (show: boolean) => void;
  setPendingPaymentAfterWalletConnect: (pending: boolean) => void;

  // Batch updates (replaces updateTxState)
  updateTransactionState: (updates: Partial<{
    txStatus: TransactionStatus;
    txHash: string;
    txError: string;
    showQR: boolean;
  }>) => void;

  // Transaction actions
  handleWalletPayment: (paymentSession?: PaymentSession, network?: string, isConnected?: boolean) => Promise<void>;
  resetTransaction: () => void;
}

export const useTransactionStore = create<TransactionState>()(
  devtools(
    (set, get) => ({
      // Initial state
      txStatus: 'idle',
      txHash: '',
      txError: '',
      showQR: false,
      pendingPaymentAfterWalletConnect: false,

      // Actions
      setTxStatus: (status) => set({ txStatus: status }),
      setTxHash: (hash) => set({ txHash: hash }),
      setTxError: (error) => set({ txError: error }),
      setShowQR: (show) => set({ showQR: show }),
      setPendingPaymentAfterWalletConnect: (pending) => set({ pendingPaymentAfterWalletConnect: pending }),

      // Batch updates
      updateTransactionState: (updates) => set((state) => ({
        ...state,
        ...updates
      })),

      // Transaction actions
      handleWalletPayment: async (paymentSession?: PaymentSession, network?: string, isConnected?: boolean) => {
        const { txStatus } = get();

        if (txStatus !== 'idle') return;
        if (!paymentSession) {
          console.warn('No payment session provided to handleWalletPayment');
          return;
        }
        if (!isConnected) {
          console.warn('Wallet not connected for handleWalletPayment');
          return;
        }

        try {
          set({ txStatus: 'preparing' });

          // Only handle EVM networks for now
          const evmNetworks = ['Ethereum', 'Arbitrum', 'Base', 'Polygon', 'BNB', 'Avalanche'];
          if (!evmNetworks.includes(paymentSession.network)) {
            set({
              txStatus: 'error',
              txError: `${paymentSession.network} payments require manual QR code scan. Please use the QR Code button.`
            });
            return;
          }

          if (!paymentSession.chainId) {
            throw new Error(`No chain ID provided for ${paymentSession.network} payment`);
          }

          set({ txStatus: 'awaiting-signature' });

          // Send the transaction
          const txHash = await sendEVMTransaction({
            chainId: paymentSession.chainId,
            toAddress: paymentSession.paymentAddress,
            amount: paymentSession.amountBaseUnits || paymentSession.amount.toString(),
            tokenAddress: paymentSession.tokenAddress,
            isBaseUnits: !!paymentSession.amountBaseUnits,
            decimals: paymentSession.decimals
          });

          set({ 
            txStatus: 'broadcasting',
            txHash: txHash
          });

          // sendEVMTransaction waits for confirmation, so if we get here it was successful
          set({ 
            txStatus: 'success'
          });

        } catch (error) {
          console.error('Wallet payment failed:', error);
          
          let errorMessage = 'Transaction failed';
          if (error instanceof EVMTransactionError) {
            switch (error.code) {
              case 'USER_REJECTED':
                errorMessage = 'Transaction was rejected by user';
                break;
              case 'INSUFFICIENT_FUNDS':
                errorMessage = 'Insufficient funds for transaction';
                break;
              case 'WRONG_CHAIN':
                errorMessage = 'Wrong network selected in wallet';
                break;
              default:
                errorMessage = error.message;
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }

          set({
            txStatus: 'error',
            txError: errorMessage
          });
        }
      },

      // Cleanup
      resetTransaction: () => set({
        txStatus: 'idle',
        txHash: '',
        txError: '',
        showQR: false,
        pendingPaymentAfterWalletConnect: false
      })
    }),
    { name: 'transaction-store' }
  )
);
